import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
})

function RegisterPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')

  const { data: countData, isLoading: countLoading } = useQuery({
    queryKey: ['users', 'count'],
    queryFn: () => api.usersCount(),
  })

  const registerMutation = useMutation({
    mutationFn: () => api.register(email, name, password),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data)
      notify.success(`Admin-Account angelegt — willkommen, ${data.user.name}`)
      navigate({ to: '/' })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (countLoading) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="py-10 text-center text-text-secondary">Lade …</CardContent>
      </Card>
    )
  }

  if ((countData?.count ?? 0) > 0) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-serif text-3xl">Registrierung deaktiviert</CardTitle>
          <CardDescription>
            Diese Ember-Instanz hat bereits Nutzer. Neue Konten kann nur ein Admin anlegen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/login">
            <Button variant="secondary" className="w-full">
              Zur Anmeldung
            </Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    registerMutation.mutate()
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="font-serif text-3xl">Erst-Setup</CardTitle>
        <CardDescription>Erste Person die sich anmeldet wird automatisch Admin.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
            {registerMutation.isPending ? 'Lege Konto an …' : 'Admin-Account erstellen'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
